<template>
	<DsTabs class="c-quarterly-results-block" :overflowMenuButtonLabel="vm.BaseViewModel.OverflowMenuLabel ?? 'More tabs'">
		<DsTab v-for="(tab, index) in tabs.BaseViewModel" :name="tab.title" :id="`tab-${index}`" :key="tab.title">

			<DsBlock :grid="true">
				<div class="c-quarterly-results-block__aside">
					<div class="c-quarterly-results-block__quarter">{{ tab.model.QuarterLabel }}</div>

					<div v-if="tab.model?.CtaLinkItem?.Url && tab.model?.CtaLinkItem?.Text && !isMobile" class="c-quarterly-results-block__cta">
						<DsButton
							:iconStart="getIconStart(tab.model.CtaLinkItem)"
							:iconEnd="getIconEnd(tab.model.CtaLinkItem)"
							variant="primary"
							:href="tab.model.CtaLinkItem.Url"
							:target="tab.model.CtaLinkItem.Target"
							>
							{{ tab.model.CtaLinkItem.Text }}
						</DsButton>
					</div>
				</div>

				<div class="c-quarterly-results-block__content">
					<DsText v-if="tab.model.Title"
						html-element="h2"
						text-style="medium"
						text-type="heading-3xl"
						class="c-quarterly-results-block__title"
						>
						{{ tab.model.Title }}
					</DsText>

					<DsText v-if="tab.model.SubTitle"
						html-element="div"
						text-style="regular"
						text-type="body-sm"
						class="c-quarterly-results-block__subtitle"
						>
						{{ tab.model.SubTitle }}
					</DsText>

					<div class="c-quarterly-results-block__links ds-grid-container">
						<div class="ds-grid mq-sm:ds-grid--cols-2">
							<slot :name="`tab-${tab.id}`"></slot>
						</div>
					</div>

					<div v-if="tab.model?.CtaLinkItem?.Url && tab.model?.CtaLinkItem?.Text && isMobile" class="c-quarterly-results-block__cta">
						<DsButton
							:iconStart="getIconStart(tab.model.CtaLinkItem)"
							:iconEnd="getIconEnd(tab.model.CtaLinkItem)"
							variant="primary"
							:href="tab.model.CtaLinkItem.Url"
							:target="tab.model.CtaLinkItem.Target"
							>
							{{ tab.model.CtaLinkItem.Text }}
						</DsButton>
					</div>
				</div>
			</DsBlock>

		</DsTab>
	</DsTabs>
</template>

<script setup>
	import { DsTabs, DsTab, DsBlock, DsText, DsButton, dsUseIsMobile } from "@coloplast/design-system";
	import "@coloplast/design-system/dist/components/tabs/ds-tabs.css";

	defineProps({
		vm: {
			type: Object
		},
		tabs: {
			type: Object
		}
	});

	const { isMobile } = dsUseIsMobile();

	function getIconStart(cta) {
		let iconName = "";
		if ( cta?.IconPosition == "Start" ) {
			iconName = cta.Icon;
		} else if ( !cta?.IconPosition ) {
			iconName = "button-download"
		} else {
			iconName = undefined;
		}
		return iconName;
	}

	function getIconEnd(cta) {
		return cta?.IconPosition == "End" ? cta.Icon : undefined;
	}
</script>

<style lang="scss" scoped>
@import "multisite/styles/settings/settings";
@import "multisite/styles/tools/tools";

.c-quarterly-results-block {
	margin-block-start: ds-space(7);

	:deep {
		.ds-tabs__nav {
			position: relative;

			&::after {
				content: "";
				position: absolute;
				bottom: ds-space(2);
				left: ds-space(2);
				right: ds-space(2);
				height: 1px;
				z-index: -1;
				background: ds-color("border", "muted");
			}

		}
		.ds-tabs__nav-wrapper {
			z-index: 10;
		}
	}

	.ds-block {
		--block-padding-block-start: var(--ds-space-12);
		--block-padding-block-end: 0;
		--block-padding-inline: 0;
		background: transparent;

		@media (max-width: ds-breakpoint("md")) {
			--block-padding-block-start: var(--ds-space-10);

			:deep .ds-block__content {
				--block-grid-gap: var(--ds-space-8);
			}
		}
	}

	&__aside {
		grid-column: span 2;

		@media (min-width: ds-breakpoint("sm")) {
			grid-column: span 12;
		}
		@media (min-width: ds-breakpoint("md")) {
			grid-column: span 4;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
		}
		@media (min-width: ds-breakpoint("lg")) {
			grid-column: span 4;
		}
	}

	&__content {
		grid-column: span 2;

		@media (min-width: ds-breakpoint("sm")) {
			grid-column: span 12;
		}

		@media (min-width: ds-breakpoint("md")) {
			grid-column: span 8;
		}

		@media (min-width: ds-breakpoint("lg")) {
			grid-column: span 8;
		}
	}

	&__quarter {
		font-size: 170px;
		color: ds-color("fg", "brand");
		line-height: 100%;
	}

	&__cta {
		margin-block-start: ds-space(10);
	}

	&__title {
		margin-block-end: ds-space(2);
	}

	&__subtitle {
		color: ds-color("fg", "muted");
		margin-block-end: ds-space(10);

		@media (min-width: ds-breakpoint("sm")) {
			margin-block-end: ds-space(13);
		}
	}

	&__links {
		width: 100%;

		.ds-grid {
			row-gap: ds-gap(0);
		}
	}
}
</style>

<style lang="scss">
@import "multisite/styles/settings/settings";
@import "multisite/styles/tools/tools";

.c-quarterly-results-block__link {
	padding: ds-space(2) 0;
	border-bottom: 1px solid ds-color("border", "muted");

	.c-document-link-block__size {
		color: ds-color("fg", "muted");
		font-weight: ds-font-weight("regular");
	}
}
</style>
